import { useCallback, useEffect, useRef, useState } from 'react';

import { leverageApi } from '../api';
import type {
  LeverageC1StatItem,
  LeverageC2StatItem,
  LeverageStatsQuery,
} from '../types';
import { getErrorMessage } from '../utils';

type StatsState<T> = {
  items: T[];
  total: number;
  loading: boolean;
  error: string | null;
};

const DEFAULT_LIMIT = 50;

export function useC1Stats(initialQuery: LeverageStatsQuery = {}) {
  const reqIdRef = useRef(0);

  const [search, setSearch] = useState(initialQuery.search ?? '');
  const [limit, setLimit] = useState(initialQuery.limit ?? DEFAULT_LIMIT);
  const [offset, setOffset] = useState(initialQuery.offset ?? 0);

  const [state, setState] = useState<
    StatsState<LeverageC1StatItem> & { totalLostAmountUsdc: string; totalVoucher: number }
  >({
    items: [],
    total: 0,
    totalLostAmountUsdc: '0',
    totalVoucher: 0,
    loading: false,
    error: null,
  });

  const fetchList = useCallback(
    async (q: LeverageStatsQuery) => {
      reqIdRef.current += 1;
      const reqId = reqIdRef.current;
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const data = await leverageApi.listC1Stats(q);
        if (reqId !== reqIdRef.current) return;
        setState({
          items: data.items,
          total: data.total,
          totalLostAmountUsdc: data.totalLostAmountUsdc,
          totalVoucher: data.totalVoucher,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (reqId !== reqIdRef.current) return;
        setState((s) => ({ ...s, loading: false, error: getErrorMessage(err) }));
      }
    },
    []
  );

  useEffect(() => {
    fetchList({ search, limit, offset });
  }, [search, limit, offset, fetchList]);

  return {
    ...state,
    search,
    limit,
    offset,
    setSearch: (v: string) => {
      setOffset(0);
      setSearch(v);
    },
    setLimit,
    setOffset,
    refetch: () => fetchList({ search, limit, offset }),
  };
}

export function useC2Stats(initialQuery: LeverageStatsQuery = {}) {
  const reqIdRef = useRef(0);

  const [search, setSearch] = useState(initialQuery.search ?? '');
  const [limit, setLimit] = useState(initialQuery.limit ?? DEFAULT_LIMIT);
  const [offset, setOffset] = useState(initialQuery.offset ?? 0);

  const [state, setState] = useState<
    StatsState<LeverageC2StatItem> & {
      totalTeamVolumeUsdc: string;
      totalRewardUsdcReceived: string;
    }
  >({
    items: [],
    total: 0,
    totalTeamVolumeUsdc: '0',
    totalRewardUsdcReceived: '0',
    loading: false,
    error: null,
  });

  const fetchList = useCallback(
    async (q: LeverageStatsQuery) => {
      reqIdRef.current += 1;
      const reqId = reqIdRef.current;
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const data = await leverageApi.listC2Stats(q);
        if (reqId !== reqIdRef.current) return;
        setState({
          items: data.items,
          total: data.totalTeams,
          totalTeamVolumeUsdc: data.totalTeamVolumeUsdc,
          totalRewardUsdcReceived: data.totalRewardUsdcReceived,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (reqId !== reqIdRef.current) return;
        setState((s) => ({ ...s, loading: false, error: getErrorMessage(err) }));
      }
    },
    []
  );

  useEffect(() => {
    fetchList({ search, limit, offset });
  }, [search, limit, offset, fetchList]);

  return {
    ...state,
    search,
    limit,
    offset,
    setSearch: (v: string) => {
      setOffset(0);
      setSearch(v);
    },
    setLimit,
    setOffset,
    refetch: () => fetchList({ search, limit, offset }),
  };
}
